import { Input } from '@/components/ui/input';
import { useAddCandidateTag, useRemoveCandidateTag } from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import type { CandidateDetail } from '@/types/candidates';
import { X } from 'lucide-react';
import { useState } from 'react';

interface CandidateTagsCloudProps {
  candidate: CandidateDetail;
}

export function CandidateTagsCloud({ candidate }: CandidateTagsCloudProps) {
  // HW-1055: a candidate who revoked consent is read-only until anonymised.
  const canEdit = usePermission('recruitment.candidates.edit') && !isConsentLocked(candidate);
  const addTag = useAddCandidateTag(candidate.id);
  const removeTag = useRemoveCandidateTag(candidate.id);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = draft.trim().replace(/^#+/, '');
    if (!trimmed) return;
    setError(null);
    try {
      await addTag.mutateAsync(trimmed);
      setDraft('');
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to add tag'));
    }
  };

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <h2 className="mb-3 text-base font-medium">Tags</h2>

      {candidate.tags.length === 0 ? (
        <p className="mb-3 text-sm text-muted-foreground">No tags yet.</p>
      ) : (
        <ul className="mb-3 flex flex-wrap gap-2">
          {candidate.tags.map((tag) => (
            <li
              key={tag.id}
              className="inline-flex max-w-[200px] items-center gap-1 rounded-full border border-border bg-muted/30 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="text-muted-foreground">#</span>
              <span className="truncate" title={tag.name}>
                {tag.name}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => removeTag.mutate(tag.name)}
                  className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-foreground/10"
                  aria-label={`Remove ${tag.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="space-y-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
            onBlur={() => setError(null)}
            placeholder="Add tag (Enter)"
            disabled={addTag.isPending}
            className="h-9 text-sm"
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

